import {getTestProps} from '@benchling/js-test-utils';
import {ActionIcon, createStyles, Group, Text} from '@mantine/core';
import {useId} from '@mantine/hooks';
import React from 'react';

import {DISMISSED_AUTOMATIC_FULL_ACTIONS_NOTICE_PREFERENCE} from 'llm/chat-library/VoicePreferences';
import usePreference from 'ui-library/hooks/usePreference';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

/** Explain Full actions during Voice, regardless of how access was enabled. */
export default function VoiceFullActionsNotice({
  chatSessionId,
  isVoiceActive,
  fullAccessEnabled,
}: {
  chatSessionId: string | null;
  isVoiceActive: boolean;
  fullAccessEnabled: boolean;
}): JSX.Element | null {
  const {classes: voiceNoticeClasses} = useVoiceNoticeStyles();
  const automaticFullActionsNoticeLabelId = useId();
  const [dismissed, setDismissedAutomaticFullActionsNotice] = usePreference<boolean>(
    DISMISSED_AUTOMATIC_FULL_ACTIONS_NOTICE_PREFERENCE
  );
  if (chatSessionId == null || !isVoiceActive || !fullAccessEnabled || dismissed) {
    return null;
  }
  return (
    <Group
      align="center"
      aria-labelledby={automaticFullActionsNoticeLabelId}
      className={voiceNoticeClasses.notice}
      noWrap={true}
      role="region"
      spacing="md"
      {...getTestProps({component: 'AutomaticFullActionsNotice', element: 'root'})}
    >
      <BenchlingIcon className={voiceNoticeClasses.infoIcon} icon="info-circle" />
      <Text className={voiceNoticeClasses.text} id={automaticFullActionsNoticeLabelId}>
        Full actions were automatically enabled for Voice.
      </Text>
      <ActionIcon
        {...getTestProps({component: 'AutomaticFullActionsNotice', element: 'dismiss'})}
        aria-label="Dismiss notice"
        className={voiceNoticeClasses.dismissButton}
        onClick={() => {
          setDismissedAutomaticFullActionsNotice(true);
        }}
        size="sm"
        variant="subtle"
      >
        <BenchlingIcon icon="xmark" />
      </ActionIcon>
    </Group>
  );
}

const useVoiceNoticeStyles = createStyles((theme) => ({
  notice: {
    // Keep the notice above the cloud and dots in the input's isolated animation layer.
    position: 'relative',
    zIndex: 1,
    width: '100%',
    minWidth: 0,
    // Match ProgressiveDisclosure's small category pills; allow taller rows if text wraps.
    minHeight: 28,
    padding: `6px ${theme.spacing.md}`,
    marginBottom: theme.spacing.sm,
    color: theme.colors.gray[9],
    backgroundColor: theme.colors.blue[0],
    borderLeft: `4px solid ${theme.colors.blue[7]}`,
    borderRadius: theme.radius.sm,
  },
  infoIcon: {
    // Override Font Awesome's 1em sizing regardless of stylesheet load order.
    '&&': {
      width: 16,
      height: 16,
    },
    flexShrink: 0,
    color: theme.colors.blue[7],
  },
  text: {
    minWidth: 0,
    flex: 1,
    overflowWrap: 'anywhere',
    fontSize: theme.fontSizes.md,
    fontWeight: theme.other.fontWeights.normal,
    lineHeight: '14px',
  },
  dismissButton: {
    flexShrink: 0,
    // Keep the 28px hit target inside the padded row without increasing its height.
    marginTop: -6,
    marginBottom: -6,
    color: theme.colors.gray[5],
    '& svg': {
      width: 16,
      height: 16,
    },
  },
}));
